import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import BookAssessment from './BookAssessment';

const logoUrl = "/logo.svg";
const ACTIVE_TEXT_COLOR = "text-white"; // Was previously border color, now for text
const INACTIVE_TEXT_COLOR = "text-gray-400";
const ACTIVE_BORDER_COLOR = "border-accent-500";
const INACTIVE_BORDER_COLOR = "border-transparent";

interface NavLink {
  href: string;
  label: string;
  id: string;
  pagePath?: string;
}

// Five items plus the booking button. The practices and the named product are here;
// "Why Us" left, because a label carrying no information is not worth a nav slot.
const navLinks: NavLink[] = [
  { href: "#home", label: "Home", id: "home", pagePath: "/" },
  { href: "#services", label: "Services", id: "services", pagePath: "/" },
  { href: "/afk-development", label: "AFK Development", id: "afk-development", pagePath: "/afk-development" },
  { href: "#projects", label: "Case Studies", id: "projects", pagePath: "/" },
  { href: "/blog", label: "Blog", id: "blog", pagePath: "/blog" },
  { href: "/contact", label: "Contact", id: "contact", pagePath: "/contact" },
];

// "Home" stays in navLinks so the scroll-spy still resolves the hero section,
// but it is not offered as a link: scrolling to the top is not a navigation task.
const visibleNavLinks = navLinks.filter(link => link.id !== 'home');

// Resolved before the first render rather than in an effect: a prerendered page
// ships the active item's class string, and hydration has to match it exactly.
const sectionForPath = (pathname: string) => {
  if (pathname.startsWith('/blog')) return 'blog';
  if (pathname === '/') return 'home';
  const page = navLinks.find(link => link.pagePath === pathname && !link.href.startsWith('#'));
  return page ? page.id : '';
};

const Navbar: React.FC = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(() => sectionForPath(location.pathname));
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const { pathname, hash } = location;
    let scrollListenerAttached = false;
    setIsMenuOpen(false);

    const handleScroll = () => {
      let newActiveId = '';

      // --- Primary Candidate Search (section straddling 150px line) ---
      let primaryCandidateId = '';
      let maxVisibleHeightForPrimary = -1;

      navLinks.forEach(link => {
        if (link.pagePath === '/' && link.href.startsWith('#')) {
          const element = document.getElementById(link.id);
          if (element) {
            const rect = element.getBoundingClientRect();
            const windowHeight = window.innerHeight || document.documentElement.clientHeight;
            // Check if the section straddles the 150px line from the top
            if (rect.top <= 150 && rect.bottom >= 150) {
              const visibleTop = Math.max(0, rect.top);
              const visibleBottom = Math.min(windowHeight, rect.bottom);
              const visibleHeight = Math.max(0, visibleBottom - visibleTop);
              if (visibleHeight > maxVisibleHeightForPrimary) {
                maxVisibleHeightForPrimary = visibleHeight;
                primaryCandidateId = link.id;
              }
            }
          }
        }
      });

      if (primaryCandidateId) {
        newActiveId = primaryCandidateId;
      } else if (window.scrollY <= 50) {
        newActiveId = 'home';
      } else {
        let fallbackCandidateId = '';
        let minTopForFallback = Infinity;
        navLinks.forEach(link => {
          if (link.pagePath === '/' && link.href.startsWith('#')) {
            const element = document.getElementById(link.id);
            if (element) {
              const rect = element.getBoundingClientRect();
              // Check if the section is visible on screen
              if (rect.top < (window.innerHeight || document.documentElement.clientHeight) && rect.bottom > 0) {
                if (rect.top < minTopForFallback) {
                  minTopForFallback = rect.top;
                  fallbackCandidateId = link.id;
                }
              }
            }
          }
        });

        if (fallbackCandidateId) {
          newActiveId = fallbackCandidateId;
        } else {
          newActiveId = 'home'; // Absolute fallback if scrolled and no other section found
        }
      }

      // Use functional update to avoid stale closure on activeSection
      setActiveSection(currentActiveSection => {
        if (currentActiveSection !== newActiveId) {
          return newActiveId;
        }
        return currentActiveSection;
      });
    };

    if (pathname === '/') {
      if (hash) {
        const elementId = hash.substring(1);
        const targetLink = navLinks.find(l => l.id === elementId && l.pagePath === '/');
        if (targetLink) {
          setActiveSection(targetLink.id);
        }
      } else {
        handleScroll(); // Set initial active section on load for homepage
      }
      window.addEventListener('scroll', handleScroll, { passive: true });
      scrollListenerAttached = true;
    } else {
      setActiveSection(sectionForPath(pathname));
    }

    return () => {
      if (scrollListenerAttached) {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [location]); // Only re-run this effect if location changes

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  const desktopItemClass = (isActive: boolean) =>
    `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium cursor-pointer ${
      isActive
        ? `${ACTIVE_TEXT_COLOR} ${ACTIVE_BORDER_COLOR}`
        : `${INACTIVE_TEXT_COLOR} ${INACTIVE_BORDER_COLOR} hover:text-white hover:border-gray-300`
    }`;

  const mobileItemClass = (isActive: boolean) =>
    `flex w-full items-center min-h-[44px] px-3 rounded-md text-base font-medium cursor-pointer ${
      isActive
        ? 'bg-gray-800 text-white'
        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
    }`;

  const renderNavItem = (link: NavLink, itemClass: (isActive: boolean) => string) => {
    // Homepage section links become /#services; page links stay as-is.
    const toPath = link.pagePath === '/' && link.href.startsWith('#')
      ? `/${link.href}`
      : link.pagePath!;

    return (
      <Link
        key={link.id}
        to={toPath}
        onClick={() => {
          setIsMenuOpen(false);
          // Set active section optimistically.
          // The useEffect will handle scrolling for hash links after navigation.
          setActiveSection(link.id);
        }}
        className={itemClass(activeSection === link.id)}
      >
        {link.label}
      </Link>
    );
  };

  return (
    <nav className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" onClick={() => setActiveSection('home')} className="inline-flex items-center min-h-[44px]">
                <img className="h-8 w-auto" src={logoUrl} width="131" height="26" alt="Kult.io" />
              </Link>
            </div>
            <div className="hidden lg:ml-10 lg:flex lg:space-x-8">
              {visibleNavLinks.map((link) => renderNavItem(link, desktopItemClass))}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Below sm the header keeps only the logo and the hamburger: the full
                label will not sit beside them at 360. The menu carries it instead. */}
            <div className="hidden sm:block">
              <BookAssessment placement="nav" variant="nav" band="gray-900" />
            </div>
            <button
              type="button"
              onClick={() => setIsMenuOpen(open => !open)}
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-md text-gray-300 hover:text-white hover:bg-gray-800 transition-colors duration-150"
              aria-label={isMenuOpen ? 'Close main menu' : 'Open main menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      <div id="mobile-menu" hidden={!isMenuOpen} className="lg:hidden border-t border-gray-800 bg-gray-900">
        <div className="px-2 py-3 space-y-1">
          {visibleNavLinks.map((link) => renderNavItem(link, mobileItemClass))}
          <div className="mt-2 sm:hidden">
            <BookAssessment placement="nav-mobile" variant="nav" band="gray-900" className="w-full" />
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
